import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ProfileCardTrigger } from '@atlaskit/profilecard';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { type NLPSearchWhoResultCollaboratorDataType } from '../../ai-answer-dialog/types';
import { messages } from '../../messages';
import { useMeaningfulHover } from '../../v2/common/hooks/use-meaningful-hover';
import { KnowledgeCardsRowRefresh } from '../knowledge-cards-row';
import { KnowledgeCardsIntentType, KnowledgeCardsRowType } from '../types';
import { useTeamsAppLinks } from '../use-teams-app-links';
import { createProfileClient, trimARI } from '../util';

type KnowledgeCardUserRowProps = {
	item: NLPSearchWhoResultCollaboratorDataType;
	maxItems: number;
	onClickCallback?: () => void;
};

export const KnowledgeCardUserRow: React.MemoExoticComponent<({ item, onClickCallback }: KnowledgeCardUserRowProps) => React.JSX.Element> = React.memo(
	({ item: user, onClickCallback }: KnowledgeCardUserRowProps): React.JSX.Element => {
		const { commonAttributes } = useAIAnswerContext();
		const { toUser } = useTeamsAppLinks({
			type: KnowledgeCardsIntentType.PERSON,
			currentUserId: user.accountId || '',
			cloudId: commonAttributes.cloudId,
		});

		const { formatMessage } = useIntl();

		const userUrl = `/people/${trimARI(user.accountId || '')}`;

		const handleUserClick = (e: React.MouseEvent) => {
			if (fg('platform-adopt-teams-nav-config')) {
				toUser(user.accountId || '').onNavigate(e);
			} else {
				window.open(user.url || userUrl, '_self', 'noopener, noreferrer');
			}
		};

		const profileClient = useMemo(
			() => createProfileClient(commonAttributes.cloudId),
			[commonAttributes.cloudId],
		);

		const { handleHoverStart, handleHoverLeave } = useMeaningfulHover({
			elementType: 'collaborator',
			hoverCardSelector: '[data-testid="profilecard"]',
		});

		return (
			<ProfileCardTrigger
				userId={trimARI(user.accountId || '')}
				resourceClient={profileClient}
				actions={[
					{
						label: formatMessage(messages.knowledge_cards_person_hover_profile_link),
						link: toUser(user.accountId || '').href,
						callback: (e) => {
							onClickCallback?.();
							toUser(user.accountId || '').onNavigate(e);
						},
					},
				]}
				ariaLabel={user.name}
				position="bottom-end"
			>
				<Box onMouseEnter={handleHoverStart} onMouseLeave={handleHoverLeave}>
					<KnowledgeCardsRowRefresh
						primaryText={user.name}
						secondaryText={
							!!user.title || !!user.department
								? [user.title, user.department].filter((str) => !!str).join(', ')
								: ''
						}
						pictureUrl={user.picture || ''}
						type={KnowledgeCardsRowType.PERSON}
						onClick={(e: React.MouseEvent<HTMLDivElement>) => {
							onClickCallback?.();
							handleUserClick(e);
						}}
					/>
				</Box>
			</ProfileCardTrigger>
		);
	},
);
